import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { InsertMembraneBody } from "../../../../../http/patrol/types/membranes";
import { useMembranesApi } from "../../../../../http/patrol/useMembranes";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

interface InsertMembraneForm {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
}

const NewMembrane: FC = () => {
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const membranesApi = useMembranesApi();

  const onSubmit = () => async (formData: InsertMembraneForm) => {
    const body: InsertMembraneBody = {
      name: formData.name!,
      nameDe: null,
      nameEs: null,
      nameFr: null,
      nameIt: null,
      order: formData.order!,
      visibility: formData.visibility,
    };

    setIsSubmitting(true);

    try {
      const res = await membranesApi.insertAdminMembrane(body);
      navigate(`/admin/systems/${systemId}/roof_substructures/${res.id}`);
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<InsertMembraneForm>
      defaultValues={{
        name: null,
        order: 50,
        visibility: "hidden",
      }}
      submitText={i18n.translation.common.save}
      disabled={isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue }) => {
        return (
          <FormFieldsContainer fullWidth>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isSubmitting}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default NewMembrane;
